import { WORDLIST, WORDLIST_SIZE } from '../data/wordlist';

/**
 * Recovery codes and trip join codes.
 *
 * A recovery code is three words - GALAH-JARRAH-ESKY - and nothing else. It is
 * optimised for being remembered and read aloud in a car, which is the entire
 * reason the feature exists.
 *
 * The trade-off, stated plainly: 1409^3 is about 2.8 billion combinations
 * (~2^31). That is far weaker than a UUID, so the defence is operational rather
 * than mathematical - only a SHA-256 hash is stored, so a database dump does not
 * hand over accounts, and /api/restore is rate limited per IP. For a game whose
 * worst case is a stranger seeing someone's plate collection this is a
 * reasonable trade; single sign-on is the durable answer for anyone who wants a
 * real account.
 *
 * Trip join codes are one word plus two digits (GALAH-42) and are unique only
 * among *active* trips, so the space is released when a trip ends. They are
 * visually distinct from a three-word recovery code, so the two cannot be
 * confused.
 *
 * Friend codes are two words plus two digits (GALAH-ESKY-42) and are permanent.
 * No code of one kind ever parses as another, which matters most against the
 * recovery code: it is a credential, and someone asked for "your code" must not
 * be able to hand over the wrong one and have it work.
 */

const WORDS_IN_RECOVERY_CODE = 3;

/** Uniform random integer in [0, max) with rejection sampling - no modulo bias. */
function secureRandomInt(max: number): number {
  if (max <= 0) throw new Error('max must be positive');
  const limit = Math.floor(0xffffffff / max) * max;
  const buf = new Uint32Array(1);
  let value: number;
  do {
    crypto.getRandomValues(buf);
    value = buf[0]!;
  } while (value >= limit);
  return value % max;
}

function randomWord(): string {
  return WORDLIST[secureRandomInt(WORDLIST_SIZE)]!;
}

export function generateRecoveryCode(): string {
  return Array.from({ length: WORDS_IN_RECOVERY_CODE }, randomWord).join('-');
}

export function generateJoinCode(): string {
  const digits = String(secureRandomInt(100)).padStart(2, '0');
  return `${randomWord()}-${digits}`;
}

/**
 * Handing over a friend code is the consent: whoever enters it lands on your
 * friends board and you on theirs. That makes a guess worth something, so the
 * space is 1409^2 x 100 - about 198 million - rather than a join code's 140,900,
 * and /api/friends/add is rate limited per IP. The worst a lucky guess can do is
 * show a stranger your name and lifetime score until you remove them.
 */
export function generateFriendCode(): string {
  const digits = String(secureRandomInt(100)).padStart(2, '0');
  return `${randomWord()}-${randomWord()}-${digits}`;
}

/** Split on anything that isn't a letter or digit, so spaces and hyphens both work. */
function tokenise(input: string): string[] {
  return input.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

/**
 * Canonicalises user input to the stored form, or returns null if it isn't a
 * valid code. Case, spacing and hyphens are all forgiving; spelling is not -
 * fuzzy matching would quietly shrink the effective search space.
 */
export function normaliseRecoveryCode(input: string): string | null {
  const parts = tokenise(input);
  if (parts.length !== WORDS_IN_RECOVERY_CODE) return null;
  if (!parts.every((word) => WORDLIST.includes(word))) return null;
  return parts.join('-');
}

export function normaliseJoinCode(input: string): string | null {
  const parts = tokenise(input);
  if (parts.length !== 2) return null;
  const [word, digits] = parts as [string, string];
  if (!WORDLIST.includes(word)) return null;
  if (!/^\d{1,2}$/.test(digits)) return null;
  return `${word}-${digits.padStart(2, '0')}`;
}

export function normaliseFriendCode(input: string): string | null {
  const parts = tokenise(input);
  if (parts.length !== 3) return null;
  const [first, second, digits] = parts as [string, string, string];
  if (!WORDLIST.includes(first) || !WORDLIST.includes(second)) return null;
  if (!/^\d{1,2}$/.test(digits)) return null;
  return `${first}-${second}-${digits.padStart(2, '0')}`;
}

/** GALAH-JARRAH-ESKY. Only ever for display; storage uses the lowercase form. */
export function formatCodeForDisplay(code: string): string {
  return code.toUpperCase();
}

/** SHA-256 hex. Available in both the browser and the Workers runtime. */
export async function hashCode(code: string): Promise<string> {
  const bytes = new TextEncoder().encode(code);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * The id other players are allowed to see.
 *
 * The player id is the bearer credential for every endpoint, so it must never
 * reach anybody else's screen. Leaderboards used to carry it, which let anyone
 * who joined a trip sign in as everyone on it. This is one-way - 128 random bits
 * cannot be recovered from their hash - and the prefix keeps it from ever
 * matching a hash stored for another purpose. Sixty-four bits is ample to tell
 * apart the rows on one board.
 *
 * Both sides derive it the same way, so a client finds its own row without the
 * server having to say which one it is.
 */
export async function publicIdFor(playerId: string): Promise<string> {
  return (await hashCode(`public:${playerId}`)).slice(0, 16);
}

/** 128 bits of randomness, hex encoded. Used for player and trip ids. */
export function generateId(): string {
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return [...buf].map((b) => b.toString(16).padStart(2, '0')).join('');
}
